import React, { useState } from "react";
import {
  KButton,
  KCheckbox,
  KDialog,
  KIcon,
  KSelect,
} from "@kyburz/kdesign-system";
import { useDispatch, useSelector } from "react-redux";
import { Typography } from "@material-ui/core";
import { RootStateInterface } from "../../../../interfaces/ReduxInterface";
import { copyCompanySettings } from "../../../../apiCalls/admin/copySettings";
import { CompanySettingsCopyInterface } from "../../../../interfaces/CompanySettingsCopyInterface";
import { setAlert } from "../../../../redux/actions/actions";

const COPY_OPTIONS = {
  copy_base_settings: true,
  copy_module_permissions: true,
  copy_sms_commands: true,
  copy_report_configurations: true,
  copy_vehicle_templates: true,
  copy_device_configurations: true,
  copy_device_firmawares: true,
  copy_vehicle_models: true,
  copy_vehicle_types: true,
  copy_roles: true,
};

const COPY_OPTIONS_DESCRIPTIONS = {
  copy_base_settings: {
    label: "Base Settings",
    description:
      "Includes all settings from this company, including company internals, internal invoicing settings, lsva, winter service, reservation and notification settings.",
  },
  copy_module_permissions: {
    label: "Module Permissions",
    description: "Copies the second tab in the Org Settings.",
  },
  copy_sms_commands: {
    label: "SMS Commands",
    description: null,
  },
  copy_report_configurations: {
    label: "Report Configurations",
    description: null,
  },
  copy_vehicle_templates: {
    label: "Vehicle Templates",
    description: null,
  },
  copy_device_configurations: {
    label: "Device Configurations",
    description: null,
  },
  copy_device_firmawares: {
    label: "Device Firmwares",
    description: null,
  },
  copy_vehicle_models: {
    label: "Vehicle Models",
    description: "Will fail if target company already has vehicle models.",
  },
  copy_vehicle_types: {
    label: "Vehicle Types",
    description: "Will fail if target company already has vehicle types.",
  },
  copy_roles: {
    label: "Roles",
    description:
      "Will copy roles even if they already exist in the target company.",
  },
};

export const CopySettings = () => {
  const dispatch = useDispatch();
  const [popupOpen, setPopupOpen] = useState(false);
  const togglePopup = () => {
    setPopupOpen(!popupOpen);
  };
  const [copyFromCustomer, setCopyFromCustomer] = useState<
    { label: string; value: string } | undefined
  >(undefined);
  const [copyOptions, setCopyOptions] = useState<
    Omit<CompanySettingsCopyInterface, "source_company_id">
  >({
    ...COPY_OPTIONS,
  });
  const customers = useSelector(
    (state: RootStateInterface) => state.customers.customers,
  );
  const currentCustomer: string = useSelector(
    (state: any) => state?.customer?.currentCustomer?.shortname,
  );

  const selectCustomer = (customer) => setCopyFromCustomer(customer);
  const customersOptions = React.useMemo(
    () =>
      customers.map((c) => ({
        label: c.name,
        value: c.shortname,
      })),
    [customers],
  );

  const renderCopyOptions = Object.keys(COPY_OPTIONS).map((optionKey) => (
    <>
      <KCheckbox
        handleChange={(e) => {
          setCopyOptions({
            ...copyOptions,
            [optionKey]: e.target.checked,
          });
        }}
        name={optionKey}
        label={COPY_OPTIONS_DESCRIPTIONS[optionKey].label}
        checked={copyOptions[optionKey]}
      />
      {COPY_OPTIONS_DESCRIPTIONS[optionKey].description && (
        <Typography variant="caption" color="textSecondary" gutterBottom>
          {COPY_OPTIONS_DESCRIPTIONS[optionKey].description}
        </Typography>
      )}
    </>
  ));

  const copySettings = () => {
    if (!copyFromCustomer?.value) {
      return;
    }

    copyCompanySettings(currentCustomer, {
      ...copyOptions,
      source_company_id: copyFromCustomer?.value,
    })
      .then((res: any) => {
        if (res.data && !res?.errors?.length) {
          dispatch(
            setAlert(
              `Settings have been copied from ${copyFromCustomer?.label}`,
              "success",
            ),
          );
        } else if (res.data && res?.errors) {
          const errors = res.errors
            .map((err) => err?.error || "unknown")
            .join(", ");
          dispatch(
            setAlert(
              `Settings have been copied with some errors: ${errors}`,
              "warning",
            ),
          );
        } else {
          dispatch(
            setAlert(res?.details || "Copying has been failed", "error"),
          );
        }
      })
      .finally(() => {
        togglePopup();
        setCopyFromCustomer(undefined);
        setCopyOptions({ ...COPY_OPTIONS });
      });
  };

  return (
    <>
      <KButton
        children="Load Settings from existing Company"
        icon={<KIcon icon="copy" />}
        theme="secondary"
        onClick={togglePopup}
      />
      <KDialog
        title="Load Settings from existing Company"
        open={popupOpen}
        size="sm"
        onClose={togglePopup}
        closeIcon={true}
        primaryCTAText="Copy Settings"
        primaryCTAonClick={copySettings}
        primaryCTADisabled={
          !copyFromCustomer || copyFromCustomer?.value === currentCustomer
        }
      >
        <KSelect
          label="Copy From"
          value={copyFromCustomer?.label}
          onSelect={selectCustomer}
          elements={customersOptions}
          name="copy_from_customer"
        />
        {renderCopyOptions}
      </KDialog>
    </>
  );
};

export default CopySettings;
